import { AuthProvider } from 'react-admin';
import { jwtDecode } from 'jwt-decode';
import { devLog } from './utils/devLog';
import { tokenService } from './services/tokenService';

// Module-level flag to prevent concurrent checkAuth/logout calls from racing
let oauthRedirectInProgress = false;

// Shared OAuth redirect logic — used by both login() and checkAuth()
const startOAuthFlow = async () => {
    const codeVerifier = generatePKCE();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID || 'swagger-client';
    const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: window.location.origin + '/auth-callback',
        scope: 'email profile',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });

    localStorage.setItem(
        'redirect_after_login',
        window.location.pathname + window.location.search + window.location.hash
    );
    devLog('[authProvider] Redirecting to OAuth server');
    window.location.href = `${ssoUrl}/authorize?${params}`;
};

const hasValidToken = (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const decoded: any = jwtDecode(token);
        return decoded.exp > Date.now() / 1000;
    } catch {
        return false;
    }
};

// Mindest-Rolle fuer das Admin-Portal. Lerner / reine Studenten (ROLE_USER,
// ROLE_STUDENT, ROLE_TEACHER, ROLE_EDITOR, ROLE_MEDIA_MANAGER) gehoeren ins
// Frontend, nicht in die Admin-UI (Issue #188). Ohne diese Pruefung bekamen
// sie zwar leere Listen, kamen aber durch die Login-Schwelle.
const ADMIN_ALLOWED_ROLES = [
    'ROLE_INSTRUCTOR',
    'ROLE_CONTENT_MANAGER',
    'ROLE_ADMIN',
    'ROLE_SUPER_ADMIN',
    'ROLE_COMPANY_ADMIN',
    'ROLE_PLATFORM_ADMIN',
    'ROLE_OPERATOR',
];

export const hasAdminAccess = (token: string): boolean => {
    try {
        const decoded: any = jwtDecode(token);
        const roles: unknown = decoded.roles;
        const rolesArray = Array.isArray(roles)
            ? roles
            : roles && typeof roles === 'object'
                ? Object.values(roles as Record<string, unknown>)
                : [];
        return rolesArray.some((r: unknown) => typeof r === 'string' && ADMIN_ALLOWED_ROLES.includes(r));
    } catch {
        return false;
    }
};

const authProvider: AuthProvider = {
    login: async () => {
        devLog('[authProvider] login called');
        localStorage.removeItem('user_logged_out');

        if (hasValidToken()) {
            devLog('[authProvider] Valid token exists, resolving login');
            return Promise.resolve();
        }

        // Remove expired/invalid token
        localStorage.removeItem('token');
        devLog('[authProvider] No valid token, starting OAuth flow');
        await startOAuthFlow();
        return new Promise(() => {});
    },

    logout: (params?: any) => {
        devLog('[authProvider] logout called', params);

        // If OAuth redirect is already happening, don't interfere
        if (oauthRedirectInProgress) {
            devLog('[authProvider] OAuth redirect in progress, skipping logout');
            return Promise.resolve();
        }

        // Check if auto-login is in progress - don't interfere with the login flow
        const autoLoginInProgress = localStorage.getItem('auto_login_in_progress');
        if (autoLoginInProgress === 'true') {
            devLog('[authProvider] Auto-login in progress, not redirecting');
            return Promise.resolve();
        }

        // Check if user was already logged out - prevent infinite redirect loop
        const wasLoggedOut = localStorage.getItem('user_logged_out');
        if (wasLoggedOut === 'true') {
            devLog('[authProvider] User already logged out, not redirecting again');
            return Promise.resolve();
        }

        const token = localStorage.getItem('token');

        // If there's no token, this is an implicit logout (e.g., checkAuth failed)
        // Mark as logged out but don't redirect — let LoginPage handle it
        if (!token) {
            devLog('[authProvider] No token present, implicit logout');
            localStorage.setItem('user_logged_out', 'true');
            return Promise.resolve();
        }

        // Explicit logout with valid token — clear everything and redirect to SSO
        localStorage.removeItem('token');
        localStorage.removeItem('redirect_after_login');
        localStorage.removeItem('code_verifier');
        sessionStorage.removeItem('identity');
        localStorage.setItem('user_logged_out', 'true');

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
        const dashboardUrl = `${ssoUrl}/dashboard`;
        devLog('[authProvider] Explicit logout - redirecting to SSO dashboard');
        window.location.href = dashboardUrl;
        return new Promise(() => {}); // Never resolve
    },

    checkError: async (error) => {
        const status = error.status;
        // Only logout on 401 (unauthorized), not on 403 (forbidden)
        if (status === 401) {
            // Issue #297: vor dem harten Logout den Refresh-Token-Tausch
            // probieren. Bei 1h JWT-TTL wuerde Walter sonst stuendlich
            // rausfliegen, obwohl die SSO-Session noch laeuft.
            const refreshed = await tokenService.refreshAccessToken();
            if (refreshed) {
                // Erfolg — react-admin retried den Request beim naechsten Tick.
                return Promise.resolve();
            }
            // Refresh hat nicht geklappt (kein Refresh-Token, abgelaufen, oder
            // SSO-Fehler) → bereinigtes Logout, sonst loop.
            tokenService.removeToken();
            tokenService.removeRefreshToken();
            return Promise.reject();
        }
        // 403 means user is authenticated but not authorized - don't logout
        return Promise.resolve();
    },

    checkAuth: (params?: any) => {
        // If we're already redirecting to SSO, all concurrent checks should wait
        if (oauthRedirectInProgress) {
            return new Promise(() => {});
        }

        // Allow access to public routes without authentication
        const publicRoutes = ['/register'];
        const currentPath = window.location.hash.replace('#', '').split('?')[0];

        if (publicRoutes.includes(currentPath)) {
            return Promise.resolve();
        }

        if (!hasValidToken()) {
            // If auto-login is in progress, redirect to SSO immediately
            // This prevents the checkAuth → logout → checkAuth loop
            const autoLoginInProgress = localStorage.getItem('auto_login_in_progress') === 'true';
            if (autoLoginInProgress) {
                devLog('[authProvider] checkAuth: No token, auto-login — redirecting to SSO');
                oauthRedirectInProgress = true;
                localStorage.removeItem('auto_login_in_progress');
                localStorage.removeItem('token');
                startOAuthFlow();
                return new Promise(() => {});
            }

            devLog('[authProvider] checkAuth: No valid token');
            localStorage.removeItem('token');
            return Promise.reject({ message: false });
        }

        // Issue #188: Admin-Portal nur fuer Rollen mit Verwaltungs-Aufgaben.
        // Lerner / reine Studenten kommen hier sonst ueber den OAuth-Flow durch
        // und landen auf einer leeren / fehlerhaften Admin-Oberflaeche.
        const token = localStorage.getItem('token')!;
        if (!hasAdminAccess(token)) {
            devLog('[authProvider] checkAuth: Token gueltig, aber keine Admin-Rolle');
            localStorage.removeItem('token');
            localStorage.removeItem('auto_login_in_progress');
            return Promise.reject({
                message: 'Dieses Portal ist Lehrenden und Administratoren vorbehalten. Bitte nutzen Sie die Lernplattform.',
                redirectTo: '/login',
            });
        }

        localStorage.removeItem('auto_login_in_progress');
        return Promise.resolve();
    },

    getPermissions: () => {
        const token = localStorage.getItem('token');
        if (!token) {
            devLog('[authProvider] getPermissions: No token found');
            return Promise.resolve([]);
        }

        try {
            const decoded: any = jwtDecode(token);
            devLog('[authProvider] getPermissions: Decoded token:', decoded);

            // Combine both permissions and roles for easy permission checks
            const permissions = decoded.permissions || [];
            const roles = decoded.roles || [];

            // Roles might be an object instead of array - convert it
            let rolesArray = roles;
            if (roles && typeof roles === 'object' && !Array.isArray(roles)) {
                rolesArray = Object.values(roles);
            }

            const permissionsArray = Array.isArray(permissions) ? permissions :
                                     (permissions && typeof permissions === 'object' ? Object.values(permissions) : []);

            const combined = [...permissionsArray, ...rolesArray];
            devLog('[authProvider] getPermissions: Combined permissions/roles:', combined);

            return Promise.resolve(combined);
        } catch (error) {
            devLog('[authProvider] getPermissions: Error decoding token:', error);
            return Promise.resolve([]);
        }
    },

    getIdentity: async () => {
        const token = localStorage.getItem('token');
        if (!token) return Promise.reject();

        // Check if we have cached identity
        const cachedIdentity = sessionStorage.getItem('identity');
        if (cachedIdentity) {
            try {
                return Promise.resolve(JSON.parse(cachedIdentity));
            } catch (e) {
                // Invalid cache, continue to fetch
            }
        }

        try {
            // Fetch user info from /api/userinfo endpoint to get numeric ID
            const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
            const response = await fetch(`${ssoUrl}/api/userinfo`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                return Promise.reject();
            }

            const userInfo = await response.json();

            const identity = {
                id: userInfo.id, // Numeric ID from backend
                uuid: userInfo.sub, // UUID
                fullName: userInfo.email,
                firstName: userInfo.firstName || userInfo.firstname || userInfo.first_name || '',
                lastName: userInfo.lastName || userInfo.lastname || userInfo.last_name || '',
                email: userInfo.email || '',
                companyId: userInfo.companyId || userInfo.company_id || userInfo.company?.id || null,
                avatar: userInfo.avatarUrl ? `${ssoUrl}${userInfo.avatarUrl}` : undefined,
            };

            // Cache the identity
            sessionStorage.setItem('identity', JSON.stringify(identity));

            return Promise.resolve(identity);
        } catch (error) {
            console.error('[authProvider] getIdentity error:', error);
            return Promise.reject();
        }
    },
};

function generatePKCE(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const arrayString = Array.from(array, byte => String.fromCharCode(byte)).join('');
    const codeVerifier = btoa(arrayString)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

    localStorage.setItem('code_verifier', codeVerifier);
    return codeVerifier;
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hash), byte => String.fromCharCode(byte)).join('');
    const base64 = btoa(hashArray)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    return base64;
}

export default authProvider;
